import { makeAutoObservable, observable, runInAction } from 'mobx';
import { z } from 'zod';

export const MAX_BYTES = 1024 * 1024;
export const widthSchema = z.number().int().min(1).max(32);
export const limitSchema = z.number().int().min(1).max(MAX_BYTES);
export const configSchema = z.object({
  source: z.string().min(1).max(80),
  width: widthSchema,
  limit: limitSchema,
  pin: z.number().int().min(0).max(35),
});
export type Config = z.infer<typeof configSchema>;
export interface Source {
  bytes: Uint8Array;
  name: string;
}

/** Session memory only: never serialize bytes, handles, paths, or file names. */
export class FileSources {
  entries = new Map<string, Source>();
  private revisions = new Map<string, number>();
  constructor() {
    makeAutoObservable(this, { entries: observable.shallow });
  }
  async select(id: string, file: File, limit: number) {
    limitSchema.parse(limit);
    const revision = (this.revisions.get(id) ?? 0) + 1;
    this.revisions.set(id, revision);
    const bytes = new Uint8Array(await file.slice(0, limit).arrayBuffer());
    if (this.revisions.get(id) !== revision) return;
    const total = [...this.entries].reduce(
      (sum, [key, value]) => sum + (key === id ? 0 : value.bytes.length),
      bytes.length,
    );
    if (total > 16 * MAX_BYTES)
      throw new Error('Session file limit is 16 MiB. Forget another file first.');
    runInAction(() => this.entries.set(id, { bytes, name: file.name }));
  }
  forget(id: string) {
    this.revisions.set(id, (this.revisions.get(id) ?? 0) + 1);
    this.entries.delete(id);
  }
}
const sessions = new WeakMap<object, FileSources>();
export function fileSources(context: object): FileSources {
  let sources = sessions.get(context);
  if (!sources) sessions.set(context, (sources = new FileSources()));
  return sources;
}

export class Reader {
  cursor = 0;
  word = 0;
  validBits = 0;
  constructor(
    readonly bytes: Uint8Array,
    readonly width: number,
  ) {
    widthSchema.parse(width);
  }
  get eof() {
    return this.cursor >= this.bytes.length * 8;
  }
  reset() {
    this.cursor = 0;
    this.word = 0;
    this.validBits = 0;
  }
  advance() {
    if (this.eof) return;
    this.word = 0;
    this.validBits = Math.min(this.width, this.bytes.length * 8 - this.cursor);
    for (let bit = 0; bit < this.validBits; bit++) {
      const position = this.cursor + bit;
      if ((this.bytes[position >> 3] & (1 << (position & 7))) !== 0)
        this.word += 2 ** bit;
    }
    this.cursor += this.validBits;
  }
}
